/**
 * RTL (Right-to-Left) Support Module
 *
 * Provides utilities for language-specific styling including:
 * - RTL language detection
 * - Language-specific CSS class generation
 * - HTML dir attribute management
 *
 * @module i18n/rtl-support
 * @see SPEC-017 Task 13, Requirements 12.1-12.5
 */

/**
 * Text direction type.
 */
export type TextDirection = 'ltr' | 'rtl';

/**
 * List of RTL (Right-to-Left) language codes.
 * These languages are written from right to left.
 *
 * @see https://en.wikipedia.org/wiki/Right-to-left_script
 */
export const RTL_LANGUAGES: readonly string[] = Object.freeze([
  'ar', // Arabic
  'he', // Hebrew
  'fa', // Persian/Farsi
  'ur', // Urdu
  'yi', // Yiddish
  'ps', // Pashto
  'sd', // Sindhi
  'ug', // Uyghur
]);

/**
 * Check if a language code represents an RTL language.
 *
 * Handles:
 * - Simple language codes (e.g., 'ar', 'he')
 * - Language codes with region (e.g., 'ar-SA', 'he-IL')
 * - Case-insensitive matching
 *
 * @param languageCode - The language code to check (e.g., 'ar', 'ar-SA', 'en')
 * @returns true if the language is RTL, false otherwise
 *
 * @example
 * ```ts
 * isRTL('ar')     // true
 * isRTL('ar-SA')  // true
 * isRTL('en')     // false
 * isRTL('zh-TW')  // false
 * ```
 */
export function isRTL(languageCode: string | undefined | null): boolean {
  if (!languageCode) {
    return false;
  }

  // Extract the primary language code (before any region/script subtag)
  const parts = languageCode.split('-');
  const primaryCode = parts[0]?.toLowerCase() ?? '';

  return RTL_LANGUAGES.includes(primaryCode);
}

/**
 * Get the text direction for a language.
 *
 * @param languageCode - The language code to check
 * @returns 'rtl' for RTL languages, 'ltr' for LTR languages
 *
 * @example
 * ```ts
 * getTextDirection('ar')     // 'rtl'
 * getTextDirection('en')     // 'ltr'
 * ```
 */
export function getTextDirection(languageCode: string): TextDirection {
  return isRTL(languageCode) ? 'rtl' : 'ltr';
}

/**
 * Get the RTL/LTR CSS class for a language.
 *
 * @param languageCode - The language code to check
 * @returns 'rtl' for RTL languages, 'ltr' for LTR languages
 *
 * @example
 * ```ts
 * getRTLClass('ar')     // 'rtl'
 * getRTLClass('en')     // 'ltr'
 * ```
 */
export function getRTLClass(languageCode: string): 'rtl' | 'ltr' {
  return isRTL(languageCode) ? 'rtl' : 'ltr';
}

/**
 * Get the language-specific CSS class.
 *
 * @param languageCode - The language code
 * @returns CSS class in format 'lang-{code}'
 *
 * @example
 * ```ts
 * getLanguageClass('en')     // 'lang-en'
 * getLanguageClass('zh-TW')  // 'lang-zh-TW'
 * ```
 */
export function getLanguageClass(languageCode: string): string {
  return `lang-${languageCode}`;
}

// Track the current language class for cleanup
let currentLanguageClass: string | null = null;

/**
 * Update the document's direction and language-specific classes.
 *
 * This function:
 * 1. Sets the `dir` attribute on `<html>` to 'rtl' or 'ltr'
 * 2. Adds/removes 'rtl' and 'ltr' classes
 * 3. Adds/removes language-specific class (e.g., 'lang-ar', 'lang-en')
 *
 * @param languageCode - The language code to apply
 *
 * @example
 * ```ts
 * updateDocumentDirection('ar');
 * // <html dir="rtl" class="rtl lang-ar">
 *
 * updateDocumentDirection('en');
 * // <html dir="ltr" class="ltr lang-en">
 * ```
 */
export function updateDocumentDirection(languageCode: string): void {
  const html = document.documentElement;
  const direction = getTextDirection(languageCode);
  const newLanguageClass = getLanguageClass(languageCode);

  // Update dir attribute
  html.dir = direction;

  // Update direction classes
  if (direction === 'rtl') {
    html.classList.add('rtl');
    html.classList.remove('ltr');
  } else {
    html.classList.add('ltr');
    html.classList.remove('rtl');
  }

  // Update language-specific class
  if (currentLanguageClass && currentLanguageClass !== newLanguageClass) {
    html.classList.remove(currentLanguageClass);
  }
  html.classList.add(newLanguageClass);
  currentLanguageClass = newLanguageClass;
}
